import React, { useState } from 'react';
import { Copy, Check, Share2, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ShareLinksPage = () => {
  const [copiedLink, setCopiedLink] = useState(null);
  
  const BASE_URL = window.location.origin;
  
  const pages = [
    { path: '/', name: 'Homepage', description: 'Main landing page' },
    { path: '/product', name: 'Product', description: 'Product features overview' },
    { path: '/pricing', name: 'Pricing', description: 'Pricing plans' },
    { path: '/pricing-check', name: 'Pricing Health Check', description: 'Free pricing tool' },
    { path: '/industries', name: 'Industries', description: 'Industries we serve' },
    { path: '/case-studies', name: 'Case Studies', description: 'Customer success stories' },
    { path: '/about', name: 'About', description: 'About AxioSera' },
    { path: '/contact', name: 'Contact', description: 'Get in touch' },
    { path: '/strategy', name: 'SMB Playbook', description: 'Pricing strategy guide' },
    { path: '/ted-talk', name: 'TED Talk', description: 'Why businesses are pricing blind' },
    { path: '/shopify-talk', name: 'Shopify Talk', description: 'For Shopify merchants' },
    { path: '/coming-soon', name: 'Coming Soon', description: 'Launch signup' },
    { path: '/partners', name: 'Partners', description: 'Partner program' },
    { path: '/insights', name: 'Insights', description: 'Blog & articles' },
    { path: '/roi-calculator', name: 'ROI Calculator', description: 'Calculate savings' },
  ];

  const getShareableLink = (path) => {
    const cleanPath = path === '/' ? '' : path.slice(1);
    return `${BASE_URL}/api/og-html/${cleanPath}`;
  };

  const copyToClipboard = async (link, path) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLink(path);
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      <section className="pt-40 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-full px-6 py-2 mb-6">
              <Share2 className="w-4 h-4 text-[#2563EB]" />
              <span className="text-[#2563EB] font-medium text-sm">Social Media Sharing</span>
            </div>
            <h1 className="text-4xl font-bold text-[#F8FAFC] mb-4">
              Shareable Links
            </h1>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              Use these links when sharing on social media (WhatsApp, LinkedIn, Twitter, Facebook, Slack). 
              They display the correct preview image and title for each page.
            </p>
          </div>

          <div className="bg-[#111827] border border-[#2563EB]/20 rounded-2xl p-6 mb-8">
            <h2 className="text-lg font-semibold text-[#F8FAFC] mb-4">Why use these links?</h2>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="bg-[#0A0F1C] rounded-lg p-4">
                <p className="text-[#2563EB] font-medium mb-1">✓ Correct Preview Image</p>
                <p className="text-[#64748B]">Each page shows its own branded image</p>
              </div>
              <div className="bg-[#0A0F1C] rounded-lg p-4">
                <p className="text-[#2563EB] font-medium mb-1">✓ Right Title & Description</p>
                <p className="text-[#64748B]">Page-specific metadata for each link</p>
              </div>
              <div className="bg-[#0A0F1C] rounded-lg p-4">
                <p className="text-[#2563EB] font-medium mb-1">✓ Auto-Redirect</p>
                <p className="text-[#64748B]">Users land on the actual page</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {pages.map((page) => {
              const shareLink = getShareableLink(page.path);
              const isCopied = copiedLink === page.path;
              
              return (
                <div 
                  key={page.path}
                  className="bg-[#111827] border border-[#2563EB]/10 rounded-xl p-4 hover:border-[#2563EB]/30 transition-all"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-[#F8FAFC] font-medium">{page.name}</h3>
                        <span className="text-xs text-[#64748B] bg-[#0A0F1C] px-2 py-0.5 rounded">
                          {page.path}
                        </span>
                      </div>
                      <p className="text-sm text-[#64748B] truncate">{shareLink}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <a 
                        href={shareLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="p-2 text-[#64748B] hover:text-[#F8FAFC] transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <Button
                        onClick={() => copyToClipboard(shareLink, page.path)}
                        size="sm"
                        className={`${
                          isCopied 
                            ? 'bg-green-500 hover:bg-green-600' 
                            : 'bg-[#2563EB] hover:bg-[#1d4ed8]'
                        } text-white min-w-[100px]`}
                      >
                        {isCopied ? (
                          <>
                            <Check className="w-4 h-4 mr-1" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-1" />
                            Copy
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-8 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
            <h3 className="text-yellow-400 font-semibold mb-2">💡 Pro Tip</h3>
            <p className="text-[#94A3B8] text-sm">
              Bookmark this page or save the links you use most often. When sharing content on 
              social media, always use these shareable links to ensure your posts look professional 
              with the correct preview image and description.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ShareLinksPage;
